"use client";

import { ChevronsLeft, ChevronsRight } from "lucide-react";
import Link from "next/link";
import { IoStarOutline } from "react-icons/io5";

import AccountMenu from "@/components/nav/AccountMenu";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { BOOKMARKS_KEY, BOOKMARKS_TITLE } from "@/components/nav/useNavBookmarks";
import { IconMapping } from "@/components/Sidebar/IconMapping";
import {
  NAV_INK_CURRENT,
  NAV_INK_ICON_BUTTON,
  NAV_INK_OPEN,
  NAV_INK_REST,
  NAV_RAIL_WIDTH,
} from "@/components/nav/nav-styles";
import { cn } from "@/lib/utils";

const RAIL_BUTTON =
  "relative flex w-full flex-col items-center gap-1.5 rounded-lg px-0.5 py-2 text-[10px] font-medium leading-tight transition-colors";

// The menu the page belongs to is NAV_INK_CURRENT; another menu whose panel
// is open takes a white tint; the rest are muted navy until hovered.
function railButtonClass({ current = false, open = false } = {}) {
  return cn(
    RAIL_BUTTON,
    open ? NAV_INK_OPEN : NAV_INK_REST,
    current && NAV_INK_CURRENT,
  );
}

function MenuButton({
  menu,
  currentKey,
  openKey,
  onSelectMenu,
  menuButtonRef,
  tipMenuKey,
}) {
  const Icon = IconMapping[menu.iconName];
  return (
    <SimpleTooltip side="right" content={menu.menuLabel}>
      <button
        type="button"
        ref={menu.key === tipMenuKey ? menuButtonRef : undefined}
        aria-current={menu.key === currentKey ? "true" : undefined}
        aria-expanded={menu.key === openKey}
        onClick={() => onSelectMenu(menu.key)}
        className={railButtonClass({
          current: menu.key === currentKey,
          open: menu.key === openKey,
        })}
      >
        {Icon && <Icon className="h-5 w-5" />}
        <span className="line-clamp-2 max-w-full break-words text-center">
          {menu.menuLabel}
        </span>
      </button>
    </SimpleTooltip>
  );
}

/**
 * The icon rail, on the navy frame: one button per menu, Home and Bookmarks
 * above them, and at the foot the panel toggle and the account menu.
 * Bookmarks opens the panel like a menu (`onSelectMenu(BOOKMARKS_KEY)`); it is
 * left out where `bookmarks` is false. The studio's mark and
 * search sit in the top bar above it (NavTopBar). Every control names itself
 * on hover (SimpleTooltip).
 * `currentKey` is the menu the page belongs to (`aria-current`); `openKey` is
 * the menu the panel is showing, if open (`aria-expanded`). `homeHref` is
 * where Home goes: "/", or a training-mode rep's training page, since "/"
 * would only bounce them there.
 */
export default function NavRail({
  menus,
  currentKey,
  openKey,
  onSelectMenu,
  isHome,
  homeHref = "/",
  bookmarks = false,
  user,
  panelOpen,
  panelAvailable,
  onTogglePanel,
  menuButtonRef,
  tipMenuKey,
}) {
  const Home = IconMapping.IoHomeOutline;
  const menuButtonProps = {
    currentKey,
    openKey,
    onSelectMenu,
    menuButtonRef,
    tipMenuKey,
  };
  return (
    <div
      className={cn(
        "flex shrink-0 flex-col items-center gap-1 px-1.5 pb-3 pt-2.5",
        NAV_RAIL_WIDTH,
      )}
    >
      {homeHref && (
        <SimpleTooltip side="right" content="Home">
          <Link
            href={homeHref}
            aria-current={isHome ? "page" : undefined}
            className={railButtonClass({ current: isHome })}
          >
            <Home className="h-5 w-5" />
            Home
          </Link>
        </SimpleTooltip>
      )}

      {bookmarks && (
        <SimpleTooltip side="right" content={BOOKMARKS_TITLE}>
          <button
            type="button"
            aria-expanded={openKey === BOOKMARKS_KEY}
            onClick={() => onSelectMenu(BOOKMARKS_KEY)}
            className={railButtonClass({ open: openKey === BOOKMARKS_KEY })}
          >
            <IoStarOutline className="h-5 w-5" />
            {BOOKMARKS_TITLE}
          </button>
        </SimpleTooltip>
      )}

      {menus.map((menu) => (
        <MenuButton key={menu.key} menu={menu} {...menuButtonProps} />
      ))}

      <div className="mt-auto flex w-full flex-col items-center gap-2 pt-2">
        {panelAvailable && (
          <SimpleTooltip side="right" content={panelOpen ? "Hide panel" : "Show panel"}>
            <button
              type="button"
              aria-pressed={panelOpen}
              aria-label={panelOpen ? "Hide panel" : "Show panel"}
              onClick={onTogglePanel}
              className={cn(NAV_INK_ICON_BUTTON, "h-9 w-9")}
            >
              {panelOpen ? (
                <ChevronsLeft className="h-4 w-4" />
              ) : (
                <ChevronsRight className="h-4 w-4" />
              )}
            </button>
          </SimpleTooltip>
        )}
        <AccountMenu user={user} ink />
      </div>
    </div>
  );
}
